// The words /admin uses for the values the stores keep as ids.
//
// Pulled out of AdminPage.js because the EXPORTS need exactly the same
// vocabulary as the tables: a spreadsheet column reading `faculty:u1` where the
// screen said "University 1" is the kind of difference that gets noticed a term
// later, in a meeting, by somebody holding the file rather than the page.
//
// ⚠️ These are labels only. The ids are the app's and the SERVER decides which
// are storable (see api/_appProfile.js) — a faculty missing from a map here
// still appears, under its own id, because a dashboard that silently drops a
// row is worse than one with an ugly label.

// ── Time ────────────────────────────────────────────────────────────────────

/** For a table, read by a person: whatever their locale does. */
export function fmtTime(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

/**
 * For a spreadsheet, sorted by a machine: "2026-09-06 14:32".
 *
 * `fmtTime`'s locale string is right in the table and wrong in a file — a
 * column of "9/6/2026, 2:32:11 PM" sorts alphabetically, which puts October
 * before September, and means different things to different readers. ISO-ish
 * with a space instead of the T, so Excel and Sheets both parse it as a date.
 */
export function csvTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
    + `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Today, for a filename — so two exports a week apart do not collide. */
export const stamp = () => new Date().toISOString().slice(0, 10);

// ── Pathfinder ──────────────────────────────────────────────────────────────

export const FAIL_LABELS = {
  'no-route': 'No route',
  'no-accessible-route': 'No accessible route',
  'accessible-no-elevators': 'Accessible + stairs only',
  'same-place': 'Already there',
};

/**
 * The routing preferences a search carried, as one readable cell.
 *
 * ⚠️ `course` is not a routing preference like the others — it says the
 * destination was found by course code rather than typed as a room. It rides
 * here because this is the column answering "how did this search differ from a
 * plain one", and it is the only place a row can say the feature was used.
 */
export function optionTags(o = {}) {
  const t = [];
  if (o.course) t.push('Course Mode');
  if (o.accessible) t.push('Accessible');
  if (o.noElevators) t.push('Stairs only');
  if (o.tunnels) t.push('Prefer tunnels');
  if (o.noTunnels) t.push('No tunnels');
  return t.length ? t.join(', ') : '—';
}

/**
 * Whether a route search began at the mobile app's OUTDOOR AUTO-START.
 *
 * A student with a precise GPS fix who is outside every building and asks for
 * directions skips the From prompt: the app starts the route at the nearest
 * outdoor nav node (floor 0, `nav_outdoors.json`, ids like
 * `o_1784337769335_200`) and logs `from: { text: "Outdoors", nodeId }`. The
 * text alone says nothing about where on campus that was, so the Routes tab
 * offers the node id behind it. Both halves are required: a bare "Outdoors"
 * with no node is just a typed word, and every other From renders as before.
 */
export function isOutdoorStart(from) {
  return !!from && from.text === 'Outdoors'
    && typeof from.nodeId === 'string' && from.nodeId.trim() !== '';
}

/**
 * What we did about a bad route rating — the four answers, in order.
 *
 * ⚠️ `new` ("Not reviewed") is NEVER STORED. It is what a rating with no triage
 * record means, so the queue is honest without a row having to be written for
 * every rating that arrives, and moving one back to it deletes the record
 * rather than storing a value that means "nothing". Must stay in step with
 * `STATUSES` in api/_routeTriage.js — an id here that the server refuses is a
 * dropdown that silently does nothing.
 */
export const TRIAGE_STATUSES = [
  { id: 'new', label: 'Not reviewed' },
  { id: 'checking', label: 'Checking' },
  { id: 'fixed', label: 'Fixed' },
  { id: 'unknown', label: 'Unknown cause' },
];

export const TRIAGE_LABELS = Object.fromEntries(TRIAGE_STATUSES.map((s) => [s.id, s.label]));

/** The stored status of one rating — absent means nobody has looked at it yet. */
export const triageStatus = (r) => r?.triage?.status || 'new';

/** A destination, as one cell. Used by the routes, scans and ratings exports. */
export function destLabel(to) {
  if (!to) return '';
  return `${to.name || to.building || ''} ${to.room || ''}`.trim();
}

// The Nearby panel's tabs, in the words the dashboard uses. The last three
// exist only in the app, which posts into the same table — see api/_placeLog.js.
export const KIND_LABELS = {
  study: 'Study spots', library: 'Libraries', bathroom: 'Washrooms',
  busstop: 'Bus stops', 'amenity:microwave': 'Microwaves', restaurant: 'Restaurants',
};

// Which rows the "what people are looking for" table draws, in tab order. A
// kind with no events still gets a row: a zero is "nobody opened it", where a
// missing row reads as "that tab does not exist".
export const NEARBY_KINDS = Object.keys(KIND_LABELS);

// Every filter the panel offers, so a zero reads as "nobody used it" rather
// than the row being missing.
export const NEARBY_FILTERS = [
  ['accessible', 'Accessible washroom'], ['period', 'Period products'], ['gender', 'Washroom type'],
  ['capacity', 'Seats'], ['plugs', 'Plugs'], ['zone', 'Quiet / loud'], ['tables', 'Tables'],
];

// ── The mobile app's first-run questions ────────────────────────────────────

export const YEAR_LABELS = {
  '1': 'First year', '2': 'Second year', '3': 'Third year',
  '4plus': 'Fourth year or beyond', grad: 'Graduate student',
};

export const FACULTY_LABELS = {
  agriculture: 'Agricultural and Food Sciences', architecture: 'Architecture', art: 'Art',
  arts: 'Arts', business: 'Business (Asper)', dentistry: 'Dentistry', education: 'Education',
  engineering: 'Engineering (Price)', environment: 'Environment, Earth and Resources',
  extended: 'Extended Education', graduate: 'Graduate Studies', kinesiology: 'Kinesiology',
  law: 'Law', medicine: 'Medicine (Rady)', music: 'Music (Desautels)', nursing: 'Nursing',
  pharmacy: 'Pharmacy', rehab: 'Rehabilitation Sciences', science: 'Science',
  'social-work': 'Social Work', u1: 'University 1',
};

export const COMMUTE_LABELS = {
  bike: 'Bike parking', bus: 'Bus stops', dropoff: 'Drop-off zones', drive: 'Parking',
};

/** A stored platform id → what the dashboard calls it. */
export const platformLabel = (p) => (p === 'ios' ? 'iPhone' : p === 'android' ? 'Android' : (p || ''));
